import { supabase } from './supabase';
import type { VisionBoardTemplate } from './api';

export type NewVisionBoardTemplate = Omit<
  VisionBoardTemplate,
  'id' | 'created_at' | 'updated_at'
>;

const TABLE = 'vision_board_templates';

function formatSupabaseError(e: unknown) {
  // Keep a console log for debugging and return a readable message
  console.error('Supabase error:', e);
  const parts: string[] = [];
  const obj = e as Record<string, unknown> | null;
  if (obj && typeof obj.message !== 'undefined')
    parts.push(String(obj.message));
  if (obj && typeof obj.details !== 'undefined')
    parts.push(String(obj.details));
  if (obj && typeof obj.hint !== 'undefined') parts.push(String(obj.hint));
  if (parts.length === 0)
    parts.push(typeof e === 'string' ? e : JSON.stringify(e));
  return parts.join(' - ');
}

export async function listVisionBoardTemplates(
  includeInactive = true
): Promise<VisionBoardTemplate[]> {
  let query = supabase
    .from(TABLE)
    .select('*')
    .order('sort_order', { ascending: true })
    .order('created_at', { ascending: false });

  if (!includeInactive) {
    query = query.eq('active', true);
  }

  const { data, error } = await query;

  if (error) throw new Error(formatSupabaseError(error));
  return (data as VisionBoardTemplate[]) || [];
}

export async function createVisionBoardTemplate(
  input: NewVisionBoardTemplate
): Promise<VisionBoardTemplate> {
  const { data, error } = await supabase
    .from(TABLE)
    .insert(input)
    .select('*')
    .single();

  if (error) throw new Error(formatSupabaseError(error));
  return data as VisionBoardTemplate;
}

export async function updateVisionBoardTemplate(
  id: string,
  input: Partial<NewVisionBoardTemplate>
): Promise<VisionBoardTemplate> {
  const { data, error } = await supabase
    .from(TABLE)
    .update(input)
    .eq('id', id)
    .select('*')
    .single();

  if (error) throw new Error(formatSupabaseError(error));
  return data as VisionBoardTemplate;
}

export async function deleteVisionBoardTemplate(id: string): Promise<void> {
  const { error } = await supabase.from(TABLE).delete().eq('id', id);

  if (error) throw new Error(formatSupabaseError(error));
}
